
import { Component, Vue, Prop, Watch, Ref } from "vue-property-decorator";
import { namespace, Getter, State } from "vuex-class";
import vselect3 from "vselect3";
import { FeaturesList, Region, User } from "@/store/types";
import { LinkedCamera, LinkedCameraStatus } from "@/store/manual-actions/types";
import { get } from "lodash";
import Camera from "@sureview/v2-cameras";

const SessionStore = namespace("sessions");
const SiteMonitor = namespace("siteMonitor");

@Component({
	components: {
		"v-select": vselect3
	}
})
export default class ManualCameraPreview extends Vue {
	@Ref() camera: Camera;

	@Prop({ default: () => [] }) public cameraList: LinkedCamera[];
	@Prop({ default: -1 }) public eventId: number;
	@Prop({ type: Boolean, default: false }) canComplete!: boolean;
	@Prop({ default: null }) public region: Region;

	@SiteMonitor.Action createAuditRecord: ({ eventId, eventRecordTypeId, details, regionUrl }) => Promise<number>;

	@SessionStore.Action updateSession: ({ resourceId, eventId, regionId }) => Promise<void>;
	@State("User") currentUser: User;

	@Getter("getFeaturesList") featuresList: FeaturesList;

	public selectedCamera: LinkedCamera = null;
	public cameraStreamedStatus: LinkedCameraStatus = null;

	@Watch("cameraList", { deep: true })
	public onCameraListChanged(newCameraList: LinkedCamera[]) {
		if (!newCameraList || !newCameraList.length) {
			return;
		}

		// By default, camera previewed is 1st in list.
		this.selectedCamera = newCameraList[0];
	}

	// Watcher runs when camera list first loaded, or different camera selected in dropdown.
	@Watch("selectedCamera", { deep: true })
	public async onSelectedCameraChanged(newSelectedCamera: LinkedCamera) {
		if (!newSelectedCamera || !newSelectedCamera.deviceId) {
			return;
		}

		this.cameraStreamedStatus = null;

		// Preview the selected camera.
		await this.updateCameraPreview(newSelectedCamera.deviceId, this.eventId);
	}

	public get isQuickControlCamerasEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "QuickControl", "Cameras"]);
	}

	public async updateCameraPreview(deviceId: number, eventId: number) {
		// By default, camera is assumed to be successfully viewed.
		this.cameraStreamedStatus = {
			viewed: true,
			streamFailed: false,
			deviceId: deviceId
		};

		await this.updateSession({ resourceId: 3, eventId: eventId, regionId: this.region?.regionId });

		this.camera.stop();

		try {
			// Play the camera, any errors with V2Cameras, camera status changed and parent is notified.
			await this.camera.startCamera(this.selectedCamera.deviceId, eventId, true, this.region?.regionId);
		} catch (err) {
			this.cameraStreamedStatus.viewed = false;
			this.cameraStreamedStatus.streamFailed = true;
			this.$emit("camera-streamed", this.cameraStreamedStatus);
		}
	}

	// Runs when a single frame has been loaded, camera has been previewed successfully.
	public async onFrameLoaded() {
		if (this.selectedCamera && !this.canComplete) {
			await this.createAuditRecord({
				eventId: this.eventId,
				eventRecordTypeId: 147,
				details: `Camera streamed successfully, user ${this.currentUser.fullName} is allowed to complete the quick action`,
				regionUrl: this.region?.url
			});
			this.$emit("camera-streamed", this.cameraStreamedStatus);
		}
	}

	// Runs when an error has occurred in device service, notifies parent of failure to preview.
	public onVideoError() {
		if (this.selectedCamera && !this.canComplete) {
			this.cameraStreamedStatus.viewed = false;
			this.cameraStreamedStatus.streamFailed = true;
			this.$emit("camera-streamed", this.cameraStreamedStatus);
		}
	}
}
